import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject,
} from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
    AuditMode,
    FormCompletionSummary,
    FormDataResult,
    FormDefinition,
    FormFunctionResult,
    FormLayout,
    FormStateData,
    FormTemplateInstance,
    KeyValueObject,
    SystemEntity,
    WdxDestroyClass,
} from '@wdx/shared/utils';
import { Observable, Subject, combineLatest, forkJoin, iif, of } from 'rxjs';
import {
    filter,
    map,
    switchMap,
    switchMapTo,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';
import {
    FormFrameworkEvent,
    FormFrameworkLoadedEvent,
    FormFrameworkSaveFailedEvent,
    FormFrameworkSaveStartEvent,
    FormFrameworkSaveSuccessEvent,
    FormStylingOptions,
} from '../../models';

import {
    DynamicFormsFacade,
    DynamicFormsService,
    dynamicFormsActions,
    dynamicFormsReducers,
    dynamicFormsSelectors,
} from '../../+state/dynamic-forms';
import { DEFAULT_FORM_STYLING } from '../../constants';
import {
    FormConfig,
    IFormExternalActions,
    ITriggerExternalFormSave,
} from '../../interfaces';
import { FormPresentationService } from '../../services';

@Component({
    selector: 'wdx-ff-form-container',
    templateUrl: './form-framework-form-container.component.html',
    providers: [FormPresentationService],
    standalone: false,
})
export class FormFrameworkFormContainerComponent
    extends WdxDestroyClass
    implements OnInit
{
    public formPresentationService = inject(FormPresentationService);
    private actions$ = inject(Actions);
    private store$ = inject(
        Store<{
            [dynamicFormsReducers.FEATURE_KEY]: dynamicFormsReducers.State;
        }>,
    );
    private dynamicFormsService = inject(DynamicFormsService);
    private externalActionsHandler = inject(IFormExternalActions);
    private cdr = inject(ChangeDetectorRef);
    private dynamicFormsFacade = inject(DynamicFormsFacade);

    @Input() formConfig?: FormConfig;
    @Input() formTemplateId!: string;
    @Input() formTemplateEntityId!: string;
    @Input() formTemplateEntityType!: SystemEntity;
    @Input() formId!: string;
    @Input() entityId!: string;
    @Input() appId!: string;
    @Input() customEndpoint!: string;
    @Input() isQuickCreate = false;
    @Input() initialisationParams!: KeyValueObject;
    @Input() queryParams!: KeyValueObject;
    @Input() emitInsteadOfPost!: boolean;
    @Input() emitData!: any;
    @Input() modalInstanceId?: string;
    @Input() set stylingOptions(options: FormStylingOptions) {
        this.formStylingOptions = {
            ...DEFAULT_FORM_STYLING,
            ...options,
        };
    }
    @Input() readonlyMode = false;
    @Input() auditMode: AuditMode = AuditMode.Off;
    @Input() extraContext?: Record<string, any>;
    @Input() externalForm = false;
    @Input() refreshAfterSave = false;
    @Input() modalCloseOnSaveSuccess = true;
    @Input() hiddenSections?: number[];
    @Input() hideVersionSwitcher = false;
    @Input() loadFormDataOverride?: 'editMode' | 'newMode';
    @Input() extraConfig?: { subformValueChangeAlwaysEmit?: boolean };

    @Output() formTitleReceived = new EventEmitter<string | null>();
    @Output() formEvent = new EventEmitter<FormFrameworkEvent>();
    @Output() cancelClicked = new EventEmitter();

    formDefinition!: FormDefinition;

    formLayout!: FormLayout;

    formData!: FormDataResult;
    formData$!: Observable<FormDataResult>;
    formDataIsLoading$!: Observable<boolean>;
    formDataIsSaving$!: Observable<boolean>;
    formDataHasError$!: Observable<boolean>;
    formStylingOptions!: FormStylingOptions;
    triggerSave?: ITriggerExternalFormSave;
    triggerSaveDraft?: ITriggerExternalFormSave;
    refreshFormData?: FormDataResult;
    formFunctionResult?: { result: FormFunctionResult; functionName: string };
    triggerFunction$ = this.externalActionsHandler.getTriggerFunction$();
    patchFormValue$ = this.externalActionsHandler.patchFormValue$();

    getFormTemplateId$ = new Subject();
    getFormTemplateData!: any;

    getAllFormInfo$ = this.formPresentationService.formDefinition$.pipe(
        filter((formDefinition) => Boolean(formDefinition)),
        tap((formDefinition) => this.loadFormData(formDefinition)),
        switchMap((_) => {
            const CONDITIONAL_GET_DATA = this.getFormTemplateData
                ? of(this.getFormTemplateData)
                : this.formData$.pipe(
                      filter((res) => Boolean(res)),
                      take(1),
                  );

            return forkJoin([
                this.formPresentationService.formDefinition$,
                CONDITIONAL_GET_DATA,
            ]);
        }),
    );

    isLoadingData = true;
    private debugFormFrameworkEvents = false;

    constructor() {
        super();
        this.debugFormFrameworkEvents =
            localStorage.getItem('wdx-debug-formframework-events') === 'true';
    }

    ngOnInit(): void {
        this.listenTo(
            ...dynamicFormsActions.formDataCreateAndUpdateSuccessActions,
        ).subscribe(
            (
                action: Record<string, any> & {
                    formId?: string;
                    formDataInstance?: FormDataResult;
                    response?: FormDataResult;
                    saveMode?: string[] | null;
                    saveId?: string;
                    silent?: boolean;
                    data?: any;
                    value?: KeyValueObject;
                    isDelete?: boolean;
                },
            ) => {
                if (this.formId === action?.formId) {
                    const successEvent = new FormFrameworkSaveSuccessEvent({
                        formName: this.formId,
                        saveId: action?.saveId,
                        formStatus: action?.response?.status,
                        entityId: action?.response?.entityId,
                        savedFormData: action?.response?.data,
                    });
                    this.emitFormEvent(successEvent);
                    if (this.modalCloseOnSaveSuccess) {
                        this.externalActionsHandler.closeSpawnedForms(
                            successEvent,
                        );
                    }
                    this.store$.dispatch(
                        dynamicFormsActions.setFormData({
                            formId: action?.formId,
                            entityId: action?.response?.entityId as string,
                            formData: action?.response as FormDataResult,
                        }),
                    );
                    if (this.refreshAfterSave) {
                        this.refreshFormData = action.response;
                        this.cdr.markForCheck();
                    }
                }
            },
        );
        this.listenTo(
            ...dynamicFormsActions.formDataCreateAndUpdateFailureActions,
        ).subscribe((action: any) => {
            if (this.formId === action?.formId) {
                this.emitFormEvent(
                    new FormFrameworkSaveFailedEvent({ formName: this.formId }),
                );
            }
        });

        this.externalActionsHandler
            .getTriggerSave$()
            ?.pipe(takeUntil(this.destroyed$))
            .subscribe((config) => {
                this.triggerSave = {
                    id: config?.id ?? Date.now().toString(),
                    externallyTriggered: true,
                    silent: config?.silent,
                };
                this.cdr.markForCheck();
            });

        this.externalActionsHandler
            .getTriggerSaveDraft$()
            ?.pipe(takeUntil(this.destroyed$))
            .subscribe((config) => {
                this.triggerSaveDraft = {
                    id: config?.id ?? Date.now().toString(),
                    externallyTriggered: true,
                    silent: config?.silent,
                };
                this.cdr.markForCheck();
            });

        this.getFormTemplateId$
            .pipe(
                switchMap((condition) =>
                    iif(
                        () => Boolean(condition),
                        this.dynamicFormsService
                            .getFormTemplateInstance(
                                this.formTemplateId,
                                this.formTemplateEntityType,
                                this.formTemplateEntityId,
                            )
                            .pipe(
                                filter((res) => Boolean(res)),
                                tap((res) => (this.getFormTemplateData = res)),
                                take(1),
                            ),
                        of(null),
                    ),
                ),
                tap((res: FormTemplateInstance | null) => {
                    if (res) {
                        this.formId = res.formName as string;
                        this.formData = res;
                    }

                    this.formPresentationService.queryFormDefinition(
                        this.formId,
                    );
                }),
                switchMapTo(this.getAllFormInfo$),
            )
            .subscribe((res) => {
                this.formDefinition = res[0];
                this.formLayout = res[0]?.layout as FormLayout;
                this.formData = res[1];
                this.isLoadingData = false;
                this.emitFormEvent(
                    new FormFrameworkLoadedEvent({
                        formStatus: this.formData.status,
                        formName: this.formData.formName,
                        entityId: this.formData.entityId,
                        entityType: this.formData.entityType,
                        initialFormData: structuredClone(this.formData.data),
                    }),
                );
                this.cdr.detectChanges();
            });

        this.getFormTemplateId$.next(this.formTemplateId);

        this.formDataIsSaving$ = combineLatest(
            this.store$.select(dynamicFormsSelectors.getIsCreatingFormData, {
                id: this.formId,
            }),
            this.store$.select(dynamicFormsSelectors.getIsUpdatingFormData, {
                id: this.formId,
            }),
        ).pipe(
            takeUntil(this.destroyed$),
            map((values) => values.some((value) => value)),
        );

        this.formDataHasError$ = this.store$.select(
            dynamicFormsSelectors.getHasLoadingFormDataError,
            {
                formId: this.formId,
                entityId: this.entityId,
            },
        );
    }

    loadFormData(
        formDefinition: FormDefinition,
        queryParams?: KeyValueObject,
    ): void {
        if (this.getFormTemplateData || this.emitInsteadOfPost) {
            return;
        }

        if (this.loadFormDataOverride) {
            switch (this.loadFormDataOverride) {
                case 'editMode':
                    this.formData$ = this.loadDataEditMode(
                        formDefinition,
                        queryParams,
                    );
                    break;
                case 'newMode':
                    this.formData$ = this.loadDataNewMode();
                    break;

                default:
                    break;
            }
            return;
        }

        if (this.appId) {
            this.formData$ = this.loadDataAppMode(queryParams);
        } else {
            if (this.entityId) {
                this.formData$ = this.loadDataEditMode(
                    formDefinition,
                    queryParams,
                );
            }

            if (this.customEndpoint) {
                this.formData$ =
                    this.dynamicFormsService.getFormDataForCustomEndpoint(
                        this.customEndpoint,
                    );
            }

            if (
                !this.entityId &&
                !this.formTemplateId &&
                !this.customEndpoint
            ) {
                this.formData$ = this.loadDataNewMode();
            }
        }
    }

    onFormTitleReceived(formTitle: string | null): void {
        this.formTitleReceived.emit(formTitle);
    }

    onCancelClick(): void {
        this.externalActionsHandler.closeSpawnedForms();
        this.cancelClicked.emit();
    }

    onSaveClick(formData: FormStateData): void {
        this.emitFormEvent(
            new FormFrameworkSaveStartEvent({
                formName: this.formId,
                saveId: formData?.saveId,
            }),
        );
        this.saveForm(formData, true);
    }

    onSaveDraftClicked(formData: FormStateData): void {
        this.saveForm(formData, false);
    }

    onCompletionSummaryChanged(completionSummary: FormCompletionSummary): void {
        if (this.formId && this.entityId) {
            this.store$.dispatch(
                dynamicFormsActions.saveCompletionSummary({
                    formId: this.formId,
                    entityId: this.entityId,
                    completionSummary,
                }),
            );
        }
    }

    saveForm(formData: FormStateData, publish: boolean): void {
        this.formPresentationService.formDefinition$
            .pipe(
                filter((formDefinition) => Boolean(formDefinition)),
                take(1),
            )
            .subscribe((formDefinition: FormDefinition) => {
                if (this.emitInsteadOfPost) {
                    this.store$.dispatch(
                        dynamicFormsActions.emitFormData({
                            data: this.emitData,
                            value: formData.value,
                            saveId: formData.saveId,
                            isQuickCreate: this.isQuickCreate,
                        }),
                    );

                    return;
                }

                if (formDefinition.standardEndpoint && !this.customEndpoint) {
                    this.store$.dispatch(
                        dynamicFormsActions.updateStandardFormData({
                            formId: this.formId,
                            entityId: this.entityId,
                            formData: formData.value,
                            publish,
                            saveMode: formDefinition.saveModes,
                            queryParams: formData.queryParams,
                            saveId: formData.saveId,
                            silent: formData?.silent,
                            isQuickCreate: this.isQuickCreate,
                        }),
                    );
                } else {
                    if (this.entityId && !this.customEndpoint) {
                        this.store$.dispatch(
                            dynamicFormsActions.updateNonStandardFormData({
                                formId: this.formId,
                                entityId: this.entityId,
                                formData: formData.value,
                                endpointPath:
                                    formDefinition.endpointPath as string,
                                saveId: formData.saveId,
                                isQuickCreate: this.isQuickCreate,
                            }),
                        );
                    } else {
                        if (!this.customEndpoint) {
                            this.store$.dispatch(
                                dynamicFormsActions.createNonStandardFormData({
                                    formId: this.formId,
                                    formData: formData.value,
                                    endpointPath:
                                        formDefinition.endpointPath as string,
                                    saveId: formData.saveId,
                                    isQuickCreate: this.isQuickCreate,
                                }),
                            );
                        }
                    }
                }

                if (this.customEndpoint) {
                    this.store$.dispatch(
                        dynamicFormsActions.putCustomEndpointFormData({
                            formId: this.formId,
                            formData: formData.value,
                            customEndpoint: this.customEndpoint,
                            saveId: formData.saveId,
                        }),
                    );
                }
            });
    }

    emitFormEvent(event: FormFrameworkEvent): void {
        if (this.debugFormFrameworkEvents) {
            console.log('FF Event:', event);
        }
        this.dynamicFormsFacade.newFormFrameworkEvent(event);
        this.formEvent.emit(event);
    }

    onFormFunction(formFunctionAndData: {
        formFunction: string;
        formData: Record<string, any>;
    }): void {
        const functionName = formFunctionAndData.formFunction;
        this.dynamicFormsService
            .getFormFunctionResult(this.formId, {
                functionName,
                formData: formFunctionAndData.formData,
            })
            .pipe(take(1))
            .subscribe((result) => {
                this.formFunctionResult = {
                    result,
                    functionName,
                };
                this.cdr.markForCheck();
            });
    }

    private listenTo(...listenedActions: any[]): Observable<Action> {
        return this.actions$.pipe(
            takeUntil(this.destroyed$),
            filter((action: Action) =>
                listenedActions.some(
                    (listenedAction) => listenedAction.type === action.type,
                ),
            ),
        );
    }

    private loadDataAppMode(queryParams?: KeyValueObject) {
        return this.queryParams?.['appcontextid']
            ? this.dynamicFormsService.getAppFormDataWithContext(
                  this.appId,
                  this.formId,
                  this.entityId,
                  this.externalForm,
              )
            : (this.formData$ = this.dynamicFormsService.getAppFormData(
                  this.appId,
                  queryParams,
                  this.externalForm,
              ));
    }

    private loadDataEditMode(
        formDefinition: FormDefinition,
        queryParams?: KeyValueObject,
    ) {
        return this.dynamicFormsService.getFormData(
            this.formId,
            this.entityId,
            formDefinition.endpointPath,
            {
                ...this.queryParams,
                ...queryParams,
            },
        );
    }

    private loadDataNewMode() {
        return this.dynamicFormsService.getFormInitialisationData(
            this.formId,
            this.initialisationParams,
        );
    }
}
